import isEqual from 'lodash/isEqual';
import React, {
  Children,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  ReactElement,
  useEffect,
  useRef,
} from 'react';
import SwiperCore, {Autoplay, Pagination} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import useIsomorphicLayoutEffect from '../hook/useIsomorphicLayoutEffect';
import usePrevious from '../hook/usePrevious';
import {
  DEFAULT_SMALL_SIZE,
  DEFAULT_MEDIUM_SIZE,
  DEFAULT_LARGE_SIZE,
} from '../scss/util/variable';

SwiperCore.use([Pagination, Autoplay]);

export interface IProps extends ISwiperOptions, ISwiperEvents {
  showPagination?: boolean;
  paginationBulletType?: keyof typeof PAGINATION_BULLET_CLASSES;
  isFlipX?: boolean;
  responsive?: {
    small?: IResponsiveItem;
    medium?: IResponsiveItem;
    large?: IResponsiveItem;
  };
  isContainerAutoHeight?: boolean;
  slidePaddingTop?: number;
  children: ReactElement[];
  slideStyle?: CSSProperties;
  slideClassName?: string;
}

type ISwiperOptions = Pick<
  Swiper,
  | 'centeredSlides'
  | 'slidesPerView'
  | 'autoplay'
  | 'initialSlide'
  | 'longSwipesRatio'
  | 'autoHeight' // direction=vertical 일 때 동작안함
  | 'height' // pagination을 제외한 높이, direction=vertical 일 때 반드시 입력해야함
  | 'lazy'
  | 'edgeSwipeDetection'
  | 'threshold'
  | 'loop'
  | 'spaceBetween'
  | 'direction'
  | 'noSwiping'
  | 'className'
  | 'allowTouchMove'
  | 'touchStartPreventDefault'
  | 'loopedSlides'
  | 'preventInteractionOnTransition'
>;

type ISwiperEvents = Pick<
  Swiper,
  | 'onTouchStart'
  | 'onTouchMove'
  | 'onTouchEnd'
  | 'onSliderMove'
  | 'onSlideChange'
  | 'onTransitionStart'
  | 'onSlideChangeTransitionStart'
  | 'onTransitionEnd'
  | 'onSlideChangeTransitionEnd'
  | 'onSlideResetTransitionEnd'
  | 'onClick'
>;

interface IResponsiveItem {
  slidesPerView?: number;
  spaceBetween?: number;
}

export type IPageSwiper = SwiperCore;

// TODO: loop 에서 스크롤 위치가 유지되지 않는 문제가 있음
function PageSwiper(
  {
    children,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onSliderMove,
    onSlideChange,
    onTransitionStart,
    onSlideChangeTransitionStart,
    onSlideResetTransitionEnd,
    onTransitionEnd,
    onSlideChangeTransitionEnd,
    onClick,
    initialSlide = 0,
    slidePaddingTop = 0,
    allowTouchMove = true,
    className,
    slideStyle,
    slideClassName,
    ...options // swiper instance가 갱신되어야 하는 값들
  }: IProps,
  externalRef: ForwardedRef<IPageSwiper>
) {
  const swiperRef = useRef<IPageSwiper>();
  useEffect(() => {
    const swiperEl = swiperRef.current;
    onTouchStart && swiperEl?.on('touchStart', onTouchStart);
    onTouchMove && swiperEl?.on('touchMove', onTouchMove);
    onTouchEnd && swiperEl?.on('touchEnd', onTouchEnd);
    onSliderMove && swiperEl?.on('sliderMove', onSliderMove);
    onSlideChange && swiperEl?.on('slideChange', onSlideChange);
    onTransitionStart && swiperEl?.on('transitionStart', onTransitionStart);
    onSlideChangeTransitionStart &&
      swiperEl?.on('slideChangeTransitionStart', onSlideChangeTransitionStart);
    onSlideResetTransitionEnd &&
      swiperEl?.on('slideResetTransitionEnd', onSlideResetTransitionEnd);
    onTransitionEnd && swiperEl?.on('transitionEnd', onTransitionEnd);
    onSlideChangeTransitionEnd &&
      swiperEl?.on('slideChangeTransitionEnd', onSlideChangeTransitionEnd);
    onClick && swiperEl?.on('click', onClick);

    return () => {
      swiperEl?.off('touchStart');
      swiperEl?.off('touchMove');
      swiperEl?.off('touchEnd');
      swiperEl?.off('sliderMove');
      swiperEl?.off('slideChange');
      swiperEl?.off('transitionStart');
      swiperEl?.off('slideChangeTransitionStart');
      swiperEl?.off('slideResetTransitionEnd');
      swiperEl?.off('transitionEnd');
      swiperEl?.off('slideChangeTransitionEnd');
      swiperEl?.off('click');
    };
  });

  /**
   * options에 넣으면 instance가 갱신되기 때문에 상태가 바뀌면 깜빡이기 때문에, 속성을 직접 변경한다.
   * TODO: 아래 두 값을 instance 갱신 없이 update하는 더 나은 방법이 있나 찾아보자.
   */
  useEffect(() => {
    swiperRef.current && (swiperRef.current.allowTouchMove = allowTouchMove);
  }, [allowTouchMove]);

  useIsomorphicLayoutEffect(() => {
    return () => {
      if (externalRef) {
        if (typeof externalRef === 'function') {
          externalRef(null);
        } else {
          externalRef.current = null;
        }
      }
    };
  }, [externalRef]);

  // 값이 없을 때 에러나거나 동작이 이상해지는 속성들 초기값 설정
  options.slidesPerView ??= 1;
  options.spaceBetween ??= 0;
  options.showPagination ??= false;

  // height가 있으면 autoHeight 해제
  options.autoHeight = options.height ? false : options.autoHeight;

  // isContainerAutoHeight면 pagination/autoHeight 해제, horizontal 모드로 강제
  if (options.isContainerAutoHeight) {
    options.showPagination = false;
    options.autoHeight = false;
    options.direction = 'horizontal';
  }

  const prevOptions = usePrevious(options);
  setUniqueId(options, prevOptions);

  const {
    isFlipX,
    responsive,
    showPagination,
    paginationBulletType,
    height,
    isContainerAutoHeight,
    threshold = 10,
    ...swiperOptions
  } = options;

  return (
    <Swiper
      // resizeObserver는 v7부터 기본값이 true (6.x에서는 false)
      // 참고: https://github.com/nolimits4web/swiper/commit/552a7eac998b6def6deeb5c032ad02aa494135fe
      resizeObserver
      key={(options as any)[UNIQUE_ID]}
      onSwiper={swiper => {
        swiperRef.current = swiper;
        typeof externalRef === 'function'
          ? externalRef(swiper)
          : externalRef && (externalRef.current = swiper);
      }}
      {...swiperOptions}
      initialSlide={initialSlide}
      pagination={showPagination ? {el: `.${PAGINATION_DEFAULT_CLASS}`} : false}
      breakpoints={getBreakPoints(responsive)}
      dir={options.isFlipX ? 'rtl' : undefined}
      // 높이를 직접 설정해줘야 vertical 모드가 동작한다.
      // horizontal 모드에서는 pagination이 가려서 slide에만 입력한다.
      style={{
        height: isContainerAutoHeight
          ? '100%'
          : options.direction === 'vertical'
            ? `${height}px`
            : undefined,
      }}
      className={className}
      threshold={threshold}
    >
      {Children.map(children, (child, index) => (
        <SwiperSlide
          key={child.key ?? index}
          className={cn(slideClassName, {
            'swiper-no-swiping': options.noSwiping,
          })}
          style={{
            ...slideStyle,
            paddingTop: `${slidePaddingTop}px`,
            height: isContainerAutoHeight
              ? '100%'
              : height
                ? `${height}px`
                : undefined,
          }}
        >
          {child}
        </SwiperSlide>
      ))}
      {showPagination && (
        <div
          className={cn(
            PAGINATION_DEFAULT_CLASS,
            PAGINATION_BULLET_CLASSES[paginationBulletType || 'default']
          )}
        ></div>
      )}
    </Swiper>
  );
}

export default forwardRef<IPageSwiper, IProps>(PageSwiper);

const UNIQUE_ID = '__uniqueid';
function setUniqueId<T extends object>(cur: T, prev?: T) {
  (cur as any)[UNIQUE_ID] = (prev as any)?.[UNIQUE_ID] || 1;
  if (prev && !isEqual(prev, cur)) {
    (cur as any)[UNIQUE_ID] = (prev as any)[UNIQUE_ID] + 1;
  }
}

function getBreakPoints(responsive: IProps['responsive']) {
  let breakpoints: Swiper['breakpoints'];
  if (responsive) {
    breakpoints = {};
    responsive.small && (breakpoints[DEFAULT_SMALL_SIZE] = responsive.small);
    responsive.medium && (breakpoints[DEFAULT_MEDIUM_SIZE] = responsive.medium);
    responsive.large && (breakpoints[DEFAULT_LARGE_SIZE] = responsive.large);
  }
  return breakpoints;
}

const PAGINATION_DEFAULT_CLASS = 'swiper-pagination';
const PAGINATION_BULLET_CLASSES = {
  default: 'swiper-pagination-bullets',
  red: 'swiper-pagination-bullets-red', // TODO: 임시, 실제 필요한 스타일 추가시 삭제
};

export const SWIPER_SCROLL_PERF_CN = 'swiper-scroll-perf';
